import { computed, reactive, watch, toRefs } from 'vue'
import { useStore } from '@freebe/sdk/store'
import isNumber from 'lodash/isNumber'
import dayjs from 'dayjs'
import QuarterPlugin from 'dayjs/plugin/quarterOfYear'
import AdvancedFormatPlugin from 'dayjs/plugin/advancedFormat'
import CustomParsePlugin from 'dayjs/plugin/customParseFormat'
import IsBetweenPlugin from 'dayjs/plugin/isBetween'
import 'dayjs/locale/fr'
import { first } from 'lodash'

dayjs.locale('fr')
dayjs.extend(QuarterPlugin)
dayjs.extend(AdvancedFormatPlugin)
dayjs.extend(CustomParsePlugin)
dayjs.extend(IsBetweenPlugin)

const FETCHED = []

export default function useStatistics (partnerId, params = {}) {
  const { get, fetch } = useStore('@admin/partners/stats')

  const options = computed(() => ({
    partnerId: partnerId,
    codes: params.codes || [],
    quarters: params.quarters || [], 
    from: params?.from || null,
    to: params?.to || null,
  }))

  const state = reactive({
    stats: computed(() => 
      get(options.value.partnerId)
    ),

    range: computed(() => {
      let cashback = state.stats?.cashback || 0 
      let signup = state.stats?.signup || 0
      let conversion = state.stats?.conversion || 0
      let subscriptions = state.stats?.subscriptions || 0
      let codes = state.stats?.codes || []
      let cashbackUsers = state.stats?.cashback_users || 0

      if (state.stats && options.value.from) {
        const fromDate = dayjs(options.value.from)
        let toDate = dayjs(options.value.to || fromDate.toDate()).endOf('month')

        if (toDate.isBefore(fromDate)) {
          toDate = dayjs(fromDate).endOf('month')
        }

        let months = [...state.stats.months]
          .sort((a, b) => {
            return dayjs().month(a.month - 1).year(a.year).toDate() - dayjs().month(b.month - 1).year(b.year).toDate()
          })
          .filter(period => {
            return dayjs().month(period.month - 1).year(period.year).isBetween(
              fromDate,
              toDate, 
              null, 
              '[]'
            )
          })

        if (months.length) {
          cashback = months.reduce((total, month) => total += month.cashback, 0) 
          signup = months.reduce((total, month) => total += month.signup || 0, 0) 
          conversion = months.reduce((total, month) => total += month.conversion, 0) 
          conversion = months.reduce((total, month) => total += month.conversion, 0) 
          subscriptions = months.reduce((total, month) => total += month.subscriptions, 0) 
          cashbackUsers = months.reduce((total, month) => total += month.cashback_users, 0) 
        } else {
          cashback = 0
          signup = 0
          conversion = 0
          subscriptions = 0
          cashbackUsers = 0
        }

        // const fromIndex = months
        //   .findIndex((period, index) => {
        //     return dayjs().month(period.month - 1).year(period.year).format('MM-YYYY') === fromDate.format('MM-YYYY')
        //   }) 
        // let toIndex = months
        //   .findIndex(period => {
        //     return dayjs().month(period.month - 1).year(period.year).format('MM-YYYY') === toDate.format('MM-YYYY')
        //   }) 

        // if (fromIndex >= 0) {
        //   if (toIndex === -1) {
        //     toIndex = fromIndex
        //   }

        //   months = months.slice(fromIndex, toIndex + 1)

        //   cashback = months.reduce((total, month) => total += month.cashback, 0) 
        //   signup = months.reduce((total, month) => total += month.signup || 0, 0) 
        //   conversion = months.reduce((total, month) => total += month.conversion, 0) 
        //   subscriptions = months.reduce((total, month) => total += month.subscriptions, 0) 
        // } else {
        //   cashback = 0
        //   signup = 0
        //   conversion = 0
        //   subscriptions = 0
        // }
      } 

      let conversionRate = cashbackUsers * 100 / signup

      if (isNaN(conversion)) {
        conversion = 0
      }

      if (conversionRate == Infinity) {
        conversionRate = 0
      }

      return {
        cashback,
        signup,
        subscriptions,
        codes,
        conversion,
        conversionRate,
        cashbackUsers,
      }
    }),

    codes: computed(() => 
      state.range.codes.map(
        ({ code }) => ({
          label: code,
          key: 'code', 
          value: code,
        })
      )
    ),

    currentQuarter: computed(() => getQuarterStats()),
    quarterMinus1: computed(() => getQuarterStats(-1)),
    quarterMinus2: computed(() => getQuarterStats(-2)),
    quarterMinus3: computed(() => getQuarterStats(-3)),
  })

  function getQuarterStats (offset = 0) {
    let fromDate = dayjs().startOf('quarter')
  
    if (offset < 0) {
      fromDate = fromDate.subtract(Math.abs(offset), 'quarters')
    } else if (offset > 0) {
      fromDate = fromDate.add(offset, 'quarter')
    }

    if (!state.stats) {
      return { 
        quarter: fromDate.format('[T]Q YYYY'),
        cashback: 0,
        signup: 0,
        subscriptions: 0,
      }
    }
  
    const toDate = dayjs(fromDate).endOf('quarter')

    let months = [...state.stats.months].sort((a, b) => {
      return dayjs().month(a.month - 1).year(a.year).toDate() - dayjs().month(b.month - 1).year(b.year).toDate()
    })

    const firstMonth = months
      .find((period, index) => {
        return dayjs().month(period.month - 1).year(period.year).format('MM-YYYY') === fromDate.format('MM-YYYY')
      }) 
    const secondMonth = months
      .find(period => {
        return dayjs().month(period.month - 1).year(period.year).format('MM-YYYY') === dayjs(fromDate).add(1, 'months').format('MM-YYYY')
      }) 
    const thirdMonth = months
      .find(period => {
        return dayjs().month(period.month - 1).year(period.year).format('MM-YYYY') === dayjs(fromDate).add(2, 'months').format('MM-YYYY')
      }) 

    months = [firstMonth, secondMonth, thirdMonth].filter(month => !!month)

    let cashback = state.stats?.cashback || 0 
    let signup = state.stats?.signup || 0
    let subscriptions = state.stats?.subscriptions || 0

    if (months.length) {
      cashback = months.reduce((total, month) => total += month.cashback, 0) 
      signup = months.reduce((total, month) => total += month.signup || 0, 0) 
      subscriptions = months.reduce((total, month) => total += month.subscriptions, 0) 
    } else {
      cashback = 0
      signup = 0
      subscriptions = 0
    }

    return { 
      quarter: fromDate.format('[T]Q YYYY'),
      cashback,
      signup,
      subscriptions,
    }
  }

  if (!state.stats) {
    console.log({
      query: {
        partner_ids: [options.value.partnerId],
      }
    })
    fetch({
      query: {
        partner_ids: [options.value.partnerId],
      }
    })
  }

  return state
}